/* eslint-disable no-console */

import Vue from 'vue';
import firebase from 'firebase';
import VueSocketIO from 'vue-socket.io';
import SocketIO from 'socket.io-client';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import VueTippy, { TippyComponent } from 'vue-tippy/dist/vue-tippy.esm';
import VueFullscreen from 'vue-fullscreen';
import VueSession from 'vue-session';
import App from '@/App.vue';
import router from './router';
import store from './store';
import vuetify from './plugins/vuetify';

Vue.config.productionTip = false;

let app: Vue;

Vue.use(VueSession);

console.log('process.env.VUE_APP_TITLE =', process.env.VUE_APP_TITLE);
console.log('process.env.NODE_ENV =', process.env.NODE_ENV);
console.log('process.env.VUE_APP_ENV =', process.env.VUE_APP_ENV);

Vue.use(CKEditor);

Vue.use(VueTippy, {
  interactive: true,
  appendTo: () => document.getElementById('app'),
});

Vue.component('tippy', TippyComponent);

Vue.use(VueFullscreen);

const socketOptions = {
  transports: ['websocket', 'polling'],
  autoConnect: false,
};

// VUE_APP_SOCKETIO_URL viene fissata a build-time: se puntasse a un dominio
// diverso da quello da cui la SPA e' servita, gli aggiornamenti real-time
// smetterebbero di funzionare in silenzio e servirebbe un rebuild per
// correggerli. In produzione backend e frontend sono lo stesso servizio,
// quindi senza la variabile ci si connette alla stessa origine della pagina.
// La variabile serve solo in sviluppo (dev server 8080 → backend 3300).
const socketioUrl = process.env.VUE_APP_SOCKETIO_URL || window.location.origin;
console.log('socket.io URL =', socketioUrl);

Vue.use(new VueSocketIO({
  debug: true,
  connection: SocketIO(socketioUrl, socketOptions),
  vuex: {
    store,
    actionPrefix: 'SOCKET_',
    mutationPrefix: 'SOCKET_',
  },
}));

Vue.directive('blur', {
  inserted(el) {
    // eslint-disable-next-line no-param-reassign
    el.onfocus = (ev) => (ev.target as any).blur();
  },
});

// Config Firebase: NON ci sono valori di default hardcoded. I progetti Firebase
// `stonepine-*` erano del cliente precedente e non devono viaggiare nel bundle di
// Syndesia (rischio concreto: se qualcuno attivasse VUE_APP_FIREBASE senza fornire
// una config propria, il traffico finirebbe sui progetti di Stone Pine). Se Syndesia
// vorrà Firebase, userà un proprio progetto configurato via variabili d'ambiente.
const FIREBASE_ENV_KEYS = {
  apiKey: 'VUE_APP_FIREBASE_API_KEY',
  authDomain: 'VUE_APP_FIREBASE_AUTH_DOMAIN',
  databaseURL: 'VUE_APP_FIREBASE_DATABASE_URL',
  projectId: 'VUE_APP_FIREBASE_PROJECT_ID',
  storageBucket: 'VUE_APP_FIREBASE_STORAGE_BUCKET',
  messagingSenderId: 'VUE_APP_FIREBASE_MESSAGING_SENDER_ID',
  appId: 'VUE_APP_FIREBASE_APP_ID',
} as const;

function buildFirebaseConfig(): { config: Record<string, string> | null; missing: string[] } {
  const config: Record<string, string> = {};
  const missing: string[] = [];

  (Object.keys(FIREBASE_ENV_KEYS) as (keyof typeof FIREBASE_ENV_KEYS)[]).forEach((key) => {
    const envKey = FIREBASE_ENV_KEYS[key];
    const value = process.env[envKey];

    if (value) {
      config[key] = value;
    } else {
      missing.push(envKey);
    }
  });

  // measurementId (Analytics) è facoltativo
  if (process.env.VUE_APP_FIREBASE_MEASUREMENT_ID) {
    config.measurementId = process.env.VUE_APP_FIREBASE_MEASUREMENT_ID;
  }

  return { config: missing.length === 0 ? config : null, missing };
}

function initFirebase(firebaseConfig: Record<string, string>) {
  console.log('initFirebase(): firebaseConfig.projectId =', firebaseConfig.projectId);

  firebase.initializeApp(firebaseConfig);

  // eslint-disable-next-line no-prototype-builtins
  if (firebaseConfig && 'measurementId' in firebaseConfig) {
    console.log('initFirebase(): Analytics è attivo');
    Vue.prototype.$analytics = firebase.analytics();
  }

  return Promise.resolve();

  // return new Promise((resolve, reject) => {
  //   firebase.firestore().enablePersistence()
  //     .then(resolve)
  //     .catch((err) => {
  //       console.log('firebase.firestore().enablePersistence() error');
  //       console.log(err);

  //       if (err.code === 'failed-precondition') {
  //         reject(err);
  //         // Multiple tabs open, persistence can only be
  //         // enabled in one tab at a a time.
  //       } else if (err.code === 'unimplemented') {
  //         reject(err);
  //         // The current browser does not support all of
  //         // the features required to enable persistence
  //       }
  //     });
  // });
}

function mountApp() {
  if (!app) {
    app = new Vue({
      router,
      store,
      vuetify,
      render: (h) => h(App),
    }).$mount('#app');
  }
}

// Firebase (CRM, Analytics) è opzionale, spento di default. Si riattiva con
// VUE_APP_FIREBASE=true in build, fornendo ANCHE la config del progetto Firebase
// di Syndesia via variabili VUE_APP_FIREBASE_* (vedi FIREBASE_ENV_KEYS sopra) — non
// esiste alcun default: se manca anche una sola variabile richiesta, l'attivazione
// fallisce in modo esplicito (errore in console con l'elenco di cosa manca) invece
// di inizializzare Firebase con valori vuoti/incompleti.
if (process.env.VUE_APP_FIREBASE === 'true') {
  const { config: firebaseConfig, missing } = buildFirebaseConfig();

  if (!firebaseConfig) {
    console.error(
      'VUE_APP_FIREBASE=true ma la configurazione Firebase è incompleta. '
      + `Variabili d'ambiente mancanti: ${missing.join(', ')}. `
      + 'Firebase non verrà inizializzato; l\'app parte comunque senza CRM/Analytics.',
    );
    mountApp();
  } else {
    initFirebase(firebaseConfig)
      .catch((err) => {
        console.log('initFirebase() error');
        console.log(err);
      });

    firebase.auth().onAuthStateChanged((firebaseUser) => {
      console.log('firebase.auth().onAuthStateChanged()');
      console.log('firebaseUser =', firebaseUser);

      // store.dispatch('fetchUser', user);

      if (firebaseUser) {
        // store.dispatch(`users/${INIT_USERS}`);
        // store.dispatch(`quotations/${INIT_QUOTATIONS}`, user);
        // store.dispatch(`phases/${INIT_PHASES}`);
        // store.dispatch(`pricelists/${INIT_PRICELISTS}`);
        // store.dispatch(`colorlists/${INIT_COLORLISTS}`);
        // store.dispatch(`products/${INIT_PRODUCTS}`);
        // store.dispatch(`tags/${INIT_TAGS}`);
      }

      mountApp();
    });
  }
} else {
  mountApp();
}
